import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageSquareWarning, Phone } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import { FeedbackModal } from "./FeedbackModal";

export const Footer = () => {
  const { getContent } = useSiteContent();
  const { user, isAuthenticated } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  
  const isVendor = user?.account_type === "vendor";
  const isCamp = user?.account_type === "camp";

  // Simplified footer for signed-in users
  if (isAuthenticated) {
    return (
      <footer className="relative bg-gradient-to-r from-[#1E3A2F] via-[#2D5A3D] to-[#1E3A2F] text-white py-4 mt-auto" style={{ zIndex: 2 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:gap-8">
            {/* Desktop: Logo and tagline on left - hidden on mobile */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              <picture>
                <source srcSet="/logo-40.webp 1x, /logo-80.webp 2x" type="image/webp" />
                <img 
                  src="/logo-small.png" 
                  alt={getContent("site_name", "CampYid")} 
                  width="32"
                  height="32"
                  className="w-8 h-8 rounded-lg"
                />
              </picture>
              <div className="flex items-center gap-2">
                <span className="text-base font-bold whitespace-nowrap">
                  Camp<span className="text-emerald-300">Yid</span>
                </span>
                <span className="text-white/40 text-sm">—</span>
                <span className="text-white/60 text-sm whitespace-nowrap">
                  {getContent("footer_tagline_short", "Plan Smarter Camp Better")}
                </span>
              </div>
            </div>

            {/* Right side content */}
            <div className="flex-1">
              {/* Disclaimer */}
              <p className="text-white/30 text-xs text-center mb-3">
                {getContent("footer_disclaimer", "CampYid is a vendor directory service only. We do not recommend, endorse, or affiliate with any vendors or listings on this platform. Users are responsible for their own due diligence when engaging with vendors.")}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
                <p className="text-white/40">
                  {getContent("footer_copyright", `© ${new Date().getFullYear()} CampYid. All rights reserved.`)}
                </p>
                <div className="flex items-center gap-4">
                  <Link to="/privacy" className="text-white/40 hover:text-white transition-colors" data-testid="footer-privacy-link">
                    {getContent("footer_privacy_link", "Privacy Policy")}
                  </Link>
                  <Link to="/terms" className="text-white/40 hover:text-white transition-colors" data-testid="footer-terms-link">
                    {getContent("footer_terms_link", "Terms of Service")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full footer for non-authenticated users
  return (
    <footer className="relative bg-gradient-to-r from-[#1E3A2F] via-[#2D5A3D] to-[#1E3A2F] text-white py-16 mt-auto" style={{ zIndex: 2 }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <picture>
                <source srcSet="/logo-40.webp 1x, /logo-80.webp 2x" type="image/webp" />
                <img 
                  src="/logo-small.png" 
                  alt={getContent("site_name", "CampYid")} 
                  width="40"
                  height="40"
                  className="w-10 h-10 rounded-xl"
                />
              </picture>
              <span className="text-xl font-bold">
                {getContent("site_name", "CampYid").split("Yid").map((part, i) => 
                  i === 0 ? <span key={i}>{part}</span> : <span key={i}><span className="text-[#FFD93D]">Yid</span>{part.substring(3)}</span>
                )}
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              {getContent("footer_tagline", "Plan Smarter Camp Better - Find the best vendors for your summer camp.")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{getContent("footer_quick_links_title", "Quick Links")}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/search" className="text-white/60 hover:text-white transition-colors">
                  {getContent("footer_browse_listings", "Browse Listings")}
                </Link>
              </li>
              <li>
                <Link to="/category/entertainment" className="text-white/60 hover:text-white transition-colors">
                  {getContent("footer_entertainment", "Entertainment")}
                </Link>
              </li>
              <li>
                <Link to="/category/food" className="text-white/60 hover:text-white transition-colors">
                  {getContent("footer_food_catering", "Food & Catering")}
                </Link>
              </li>
              <li>
                <Link to="/category/supplies" className="text-white/60 hover:text-white transition-colors">
                  {getContent("footer_supplies", "Supplies")}
                </Link>
              </li>
            </ul>
          </div>

          {/* For Vendors */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{getContent("footer_for_vendors_title", "For Vendors")}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/register" className="text-white/60 hover:text-white transition-colors" data-testid="footer-vendor-register">
                  {getContent("footer_create_account", "Create Account")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/register" 
                  className="text-white/60 hover:text-white transition-colors"
                  data-testid="footer-submit-listing"
                >
                  {getContent("footer_submit_listing", "Submit Listing")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/pricing" 
                  className="text-white/60 hover:text-white transition-colors"
                  data-testid="footer-pricing"
                >
                  {getContent("footer_pricing_link", "Pricing")}
                </Link>
              </li>
              <li>
                <Link 
                  to="/register" 
                  className="text-white/60 hover:text-white transition-colors"
                  data-testid="footer-vendor-dashboard"
                >
                  {getContent("footer_vendor_dashboard", "Vendor Dashboard")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{getContent("footer_contact_title", "Contact")}</h4>
            <ul className="space-y-3">
              {getContent("footer_contact_phone_enabled", "true") === "true" && (
                <li>
                  <a 
                    href={`tel:${getContent("footer_contact_phone", "848-210-1820").replace(/[^0-9+]/g, '')}`}
                    className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                    data-testid="footer-phone-link"
                  >
                    <Phone className="w-4 h-4 text-emerald-400" />
                    {getContent("footer_contact_phone", "848-210-1820")}
                  </a>
                </li>
              )}
              <li>
                <a 
                  href={`mailto:${getContent("footer_contact_email", "info@campyid.com")}`}
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4 text-emerald-400" />
                  {getContent("footer_contact_email", "info@campyid.com")}
                </a>
              </li>
              <li>
                <button 
                  onClick={() => setFeedbackOpen(true)}
                  className="flex items-center gap-3 text-white/60 hover:text-white transition-colors"
                  data-testid="footer-feedback-btn"
                >
                  <MessageSquareWarning className="w-4 h-4 text-emerald-400" />
                  {getContent("footer_feedback_btn", "Feedback & Report Bug")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
          {/* Disclaimer */}
          <p className="text-white/30 text-xs text-center mb-6 max-w-3xl mx-auto">
            {getContent("footer_disclaimer", "CampYid is a vendor directory service only. We do not recommend, endorse, or affiliate with any vendors or listings on this platform. Users are responsible for their own due diligence when engaging with vendors.")}
          </p>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">
              {getContent("footer_copyright", `© ${new Date().getFullYear()} CampYid. All rights reserved.`)}
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-privacy-link">
                {getContent("footer_privacy_link", "Privacy Policy")}
              </Link>
              <Link to="/terms" className="text-white/40 hover:text-white text-sm transition-colors" data-testid="footer-terms-link">
                {getContent("footer_terms_link", "Terms of Service")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </footer>
  );
};
